import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const keywordSets = {
  brand: [
    "riyu",
    "reyou",
    "riyu app",
    "reyou app",
    "personal nutrition os",
  ],
  nutrition: [
    "ernaehrung",
    "gesunde ernaehrung",
    "personalisierte ernaehrung",
    "ernaehrungsplan",
    "ernaehrungs app",
    "nutrition app",
    "naehrwerte",
    "makros",
    "kalorien",
    "protein",
  ],
  ai: [
    "ki",
    "kuenstliche intelligenz",
    "ki app",
    "ki ernaehrung",
    "ai nutrition",
    "ai meal planning",
    "personalisierung mit ki",
  ],
  recipes: [
    "rezepte",
    "gesunde rezepte",
    "ki rezepte",
    "rezept app",
    "koch app",
    "wochenplan",
    "wochenplan rezepte",
    "einkaufsliste",
    "alltagsrezepte",
    "familienrezepte",
  ],
  apps: [
    "app",
    "mobile app",
    "ios app",
    "android app",
    "web app",
    "meal planning app",
    "ernaehrungscoach app",
  ],
  waitlist: [
    "warteliste",
    "beta",
    "beta zugang",
    "early access",
    "app launch",
  ],
  legal: [
    "datenschutz",
    "dsgvo",
    "privacy",
    "impressum",
    "agb",
    "nutzungsbedingungen",
  ],
};

function mergeKeywords(...groups) {
  const flattened = groups.flat().filter(Boolean);
  return [...new Set(flattened)].join(", ");
}

const defaultMeta = {
  title: "RIYU | Nutrition OS",
  description:
    "Personal Nutrition OS mit personalisierten Rezepten, Wochenplänen und Einkaufslisten für deinen Alltag.",
  keywords: mergeKeywords(
    keywordSets.brand,
    keywordSets.nutrition,
    keywordSets.ai,
    keywordSets.apps,
    keywordSets.recipes
  ),
  ogType: "website",
  allowIndex: false,
  pageName: "Nicht gefunden",
};

const routeMeta = {
  "/": {
    title: "RIYU | Personal Nutrition OS",
    description:
      "Personal Nutrition OS mit personalisierten Rezepten, Wochenplänen und Einkaufslisten für deinen Alltag.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps,
      keywordSets.recipes
    ),
    ogType: "website",
    allowIndex: true,
    pageName: "Startseite",
  },
  "/about": {
    title: "Über Uns | RIYU und ReYou",
    description:
      "Lerne die Vision hinter RIYU und ReYou kennen: KI-gestuetzte Ernaehrung, personalisierte Rezeptvorschlaege und eine alltagstaugliche App.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps,
      keywordSets.recipes
    ),
    ogType: "article",
    allowIndex: true,
    pageName: "Ueber Uns",
  },
  "/privacy": {
    title: "Datenschutz | RIYU",
    description:
      "Datenschutzerklaerung von RIYU by ReYou fuer unsere KI Ernaehrungs-App mit Infos zu Verarbeitung, Tracking und deinen Rechten.",
    keywords: mergeKeywords(
      keywordSets.legal,
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps
    ),
    ogType: "article",
    allowIndex: true,
    pageName: "Datenschutz",
  },
  "/agb": {
    title: "AGB | RIYU",
    description:
      "Allgemeine Geschaeftsbedingungen der ReYou UG fuer die Nutzung der RIYU KI Ernaehrungs-App und Services.",
    keywords: mergeKeywords(
      keywordSets.legal,
      keywordSets.brand,
      keywordSets.apps
    ),
    ogType: "article",
    allowIndex: true,
    pageName: "AGB",
  },
  "/impressum": {
    title: "Impressum | RIYU",
    description:
      "Impressum der ReYou UG mit Kontakt und Verantwortlichkeitsangaben fuer RIYU und ReYou.",
    keywords: mergeKeywords(keywordSets.legal, keywordSets.brand),
    ogType: "article",
    allowIndex: true,
    pageName: "Impressum",
  },
  "/servicedescription": {
    title: "Leistungsbeschreibung | RIYU",
    description:
      "Leistungsbeschreibung der RIYU Features rund um KI, Ernaehrung, Rezepte, Wochenplanung und App-Funktionen.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps,
      keywordSets.recipes
    ),
    ogType: "article",
    allowIndex: true,
    pageName: "Leistungsbeschreibung",
  },
  "/store": {
    title: "RIYU App Download | ReYou KI Ernaehrungs-App fuer iOS und Android",
    description:
      "Lade die RIYU App und starte mit KI-gestuetzter Ernaehrung, personalisierten Rezepten und smarter Wochenplanung.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.apps,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.recipes
    ),
    ogType: "website",
    allowIndex: true,
    pageName: "App Download",
  },
  "/waitlist": {
    title: "Warteliste | RIYU",
    description:
      "Danke fuer deine Anmeldung zur RIYU Warteliste fuer die KI Ernaehrungs-App mit Rezepten und Wochenplaenen.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.waitlist,
      keywordSets.apps,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.recipes
    ),
    ogType: "website",
    allowIndex: false,
    pageName: "Warteliste",
  },
  "/newsletter": {
    title: "Newsletter Bestaetigung | RIYU",
    description:
      "Danke fuer deine Newsletter-Anmeldung zu Updates rund um KI Ernaehrung, App-Features und neue Rezepte.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps,
      keywordSets.recipes
    ),
    ogType: "website",
    allowIndex: false,
    pageName: "Newsletter",
  },
  "/unsubscribe": {
    title: "Newsletter Abmeldung | RIYU",
    description:
      "Abmeldung vom RIYU Newsletter zu Themen wie Ernaehrung, KI, Rezepte und App-Updates.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps,
      keywordSets.recipes
    ),
    ogType: "website",
    allowIndex: false,
    pageName: "Abmeldung",
  },
  "/recipe": {
    title: "Personalisierte KI Rezepte | RIYU",
    description:
      "Personalisierte Rezeptansicht mit Zutaten, Schritten und Naehrwerten fuer deine KI-gestuetzte Ernaehrung.",
    keywords: mergeKeywords(
      keywordSets.brand,
      keywordSets.recipes,
      keywordSets.nutrition,
      keywordSets.ai,
      keywordSets.apps
    ),
    ogType: "article",
    allowIndex: false,
    pageName: "Rezept",
  },
};

function getSiteUrl() {
  if (process.env.REACT_APP_SITE_URL) {
    return process.env.REACT_APP_SITE_URL.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "https://reyou.ai";
}

function upsertMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    if (hreflang) {
      element.setAttribute("hreflang", hreflang);
    }
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function setStructuredData(payloads) {
  const oldNodes = document.head.querySelectorAll("script[data-seo-ld='true']");
  oldNodes.forEach((node) => node.remove());

  payloads.forEach((payload) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-ld", "true");
    script.text = JSON.stringify(payload);
    document.head.appendChild(script);
  });
}

function resolveMeta(pathname) {
  if (routeMeta[pathname]) {
    return routeMeta[pathname];
  }
  if (pathname.startsWith("/recipe")) {
    return routeMeta["/recipe"];
  }
  return defaultMeta;
}

function buildStructuredData(pathname, meta, canonicalUrl, siteUrl) {
  const data = [];
  const appDescription =
    "RIYU | Personal Nutrition OS";
  const appKeywords = mergeKeywords(
    keywordSets.brand,
    keywordSets.nutrition,
    keywordSets.ai,
    keywordSets.apps,
    keywordSets.recipes
  );

  data.push({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ReYou UG (haftungsbeschraenkt)",
    alternateName: "RIYU",
    url: siteUrl,
    logo: `${siteUrl}/rylogo.ico`,
    description: appDescription,
    sameAs: ["https://instagram.com/reyou"],
  });

  data.push({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: meta.pageName,
    headline: meta.title,
    description: meta.description,
    inLanguage: "de-DE",
    url: canonicalUrl,
    keywords: meta.keywords,
    about: ["Ernaehrung", "KI", "Rezepte", "Apps"],
    isPartOf: {
      "@type": "WebSite",
      name: "RIYU",
      url: siteUrl,
    },
  });

  if (pathname === "/") {
    data.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "RIYU",
      url: siteUrl,
      inLanguage: "de-DE",
      description: appDescription,
      keywords: appKeywords,
      about: ["Ernaehrung", "KI", "Rezepte", "Apps"],
      publisher: {
        "@type": "Organization",
        name: "ReYou UG (haftungsbeschraenkt)",
      },
    });

    data.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Warum brauche ich RIYU?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "RIYU reduziert taegliche Ernahrungsentscheidungen durch personalisierte Vorschlaege.",
          },
        },
        {
          "@type": "Question",
          name: "Wann startet RIYU?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Der Start erfolgt schrittweise. Ueber die Warteliste informieren wir sofort zum Launch.",
          },
        },
        {
          "@type": "Question",
          name: "Ist RIYU kostenlos?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Die Warteliste ist kostenlos. Preise fuer Pakete werden transparent vorab kommuniziert.",
          },
        },
      ],
    });
  }

  if (pathname === "/" || pathname === "/store") {
    data.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "RIYU",
      alternateName: "ReYou",
      applicationCategory: "HealthApplication",
      operatingSystem: "iOS, Android, Web",
      inLanguage: "de-DE",
      url: `${siteUrl}/store`,
      description: appDescription,
      featureList: [
        "Personalisierte KI Rezepte",
        "Intelligente Wochenplanung",
        "Einkaufslisten nach Plan",
        "Naehrwerte und Makro-Uebersicht",
      ],
      keywords: appKeywords,
      publisher: {
        "@type": "Organization",
        name: "ReYou UG (haftungsbeschraenkt)",
      },
    });
  }

  if (pathname !== "/") {
    data.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Startseite",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: meta.pageName,
          item: canonicalUrl,
        },
      ],
    });
  }

  return data;
}

export default function RouteSeo() {
  const { pathname } = useLocation();
  const siteUrl = useMemo(() => getSiteUrl(), []);

  useEffect(() => {
    const meta = resolveMeta(pathname);
    const canonicalPath = pathname || "/";
    const canonicalUrl = `${siteUrl}${canonicalPath}`;
    const ogImage = `${siteUrl}/rylogo.ico`;
    const robotsValue = meta.allowIndex ? "index,follow,max-image-preview:large" : "noindex,nofollow";

    document.title = meta.title;
    document.documentElement.lang = "de";

    upsertLink("canonical", canonicalUrl);
    upsertLink("alternate", canonicalUrl, "de-DE");
    upsertLink("alternate", canonicalUrl, "x-default");

    upsertMeta("name", "description", meta.description);
    upsertMeta("name", "keywords", meta.keywords);
    upsertMeta("name", "news_keywords", meta.keywords);
    upsertMeta("name", "category", "Ernaehrung, KI, Rezepte, Apps");
    upsertMeta("name", "robots", robotsValue);
    upsertMeta("name", "googlebot", robotsValue);
    upsertMeta("name", "theme-color", "#f6f7f6");

    upsertMeta("property", "og:locale", "de_DE");
    upsertMeta("property", "og:type", meta.ogType);
    upsertMeta("property", "og:site_name", "RIYU");
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:alt", "RIYU Logo");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", meta.title);
    upsertMeta("name", "twitter:description", meta.description);
    upsertMeta("name", "twitter:image", ogImage);

    const structuredData = buildStructuredData(pathname, meta, canonicalUrl, siteUrl);
    setStructuredData(structuredData);
  }, [pathname, siteUrl]);

  return null;
}
